import React, { useContext, useEffect } from "react";
import { DateCalendar, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/ru";
import dayjs from "dayjs";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import TodayIcon from "@mui/icons-material/Today";
import { formEdit, getDateForDateObj, getIntOfStrDate, getMaxDay, getStrDateOfDateObj } from "../../Tools/Tools";
import { DefaultDialogContentProps } from "./DialogAcctools";
import clsx from "clsx";
import dialogStyles from "./Components/dialogStyles.module.css";
import MonthButton from "./Components/dialogCalendarComponents/MonthButton";
import CalendarToolButton from "./Components/dialogCalendarComponents/CalendarToolsButton";
import { SmetaModalsButtonsContext } from "../../Tools/modalManager";

export default function DialogCalendar({ props }: DefaultDialogContentProps) {
  const {
    Module: module,
    Name: name,
    record,
    RequestID: requestId,
    addInfo,
    from,
    Buttons: buttons,
    hiddenButton,
    onEdit,
    setValue,
    setInputValue,
    func,
    Value: value,
    inputValue,
  } = props;

  const [dateValue, setDateValue] = React.useState(value || inputValue || getDateData(getNowDate()).label);

  const calendar = React.useRef<HTMLDivElement | null>(null);

  const setButtons = useContext(SmetaModalsButtonsContext);

  useEffect(() => {
    if (hiddenButton) return;
    const modalButtons: { [key: number]: { label: string; onClick: () => void } } = {};
    if (!buttons || Number(buttons) & 1) {
      modalButtons[1] = { label: "Выбрать", onClick: onSelect };
    }
    setButtons?.(modalButtons);
  }, [setButtons, dateValue]);

  /**
   * Меняет английские подсказки для кнопок на русские
   */
  const fixTitleMuiButtons = () => {
    if (calendar.current) {
      const prev: any = calendar.current.querySelector(".MuiIconButton-edgeEnd");
      if (prev) {
        prev.title = "Предыдущий месяц";
      }
      const next: any = calendar.current.querySelector(".MuiIconButton-edgeStart");
      if (next) {
        next.title = "Следующий месяц";
      }
    }
  };
  useEffect(fixTitleMuiButtons, [calendar.current]);

  /**
   * Возвращает сегодняшнюю дату в русском формате

   * @returns возвращает дату в виде строки, в русском формате - "dd.mm.yyyy"
   */
  function getNowDate() {
    return getStrDateOfDateObj(new Date());
  }

  /**
   * Возвращает дату в в виде объекта для редактора
   *
   * @returns объект {label:"DD.MM.YYYY", id:"dayCount"} - объект где label это строка с датой в формате DD.MM.YYYY, а id это количество дней с 31.12.1899 года
   */
  function getDateData(value: string): { label: string; id: string } {
    const date = getDateForDateObj(value);
    if (date) {
      const id = getIntOfStrDate(value);
      return { label: value === ".." ? "" : value, id: `${id}` };
    } else return { label: "", id: "" };
  }

  /**
   * Устанавливает новое состояние даты с новым значением месяца
   *
   * @param numMonth - номер месяца от 1 до 12
   */
  function monthSelect(numMonth: string) {
    const date = dateValue.split(".");
    let day = date[0];
    const month = Number(numMonth) < 10 ? `0${numMonth}` : numMonth;
    const year = date[2];
    const maxDay = getMaxDay(numMonth, year);

    if (Number(day) > maxDay) day = `${maxDay}`;

    setDateValue(`${day}.${month}.${year}`);
  }

  /**
   * Устанавливает новое состояние даты в начало текущего года
   */
  const onStartYear = () => {
    const dateString = getDateForDateObj(dateValue);
    const date = new Date(dateString || "") || new Date();
    setDateValue(`01.01.${date.getFullYear()}`);
  };

  /**
   * Устанавливает новое состояние даты на сегодняшнее число
   */
  const onCurrentDate = () => {
    setDateValue(getDateForDateObj(getNowDate()) || "");
  };

  /**
   * Устанавливает новое состояние даты в конец текущего года
   */
  const onEndYear = () => {
    const dateString = getDateForDateObj(dateValue);
    const date = new Date(dateString || "") || new Date();
    setDateValue(`31.12.${date.getFullYear()}`);
  };

  /**
   * Выполняет функцию редактирования при наличии, или при наличии RequestID отправляет данные в tokenprocessing
   */
  async function onSelect() {
    const date = getDateData(dateValue);
    if (requestId) {
      await formEdit("1", func, { Name: "ObjRef", Value: date.id }, requestId, from);
      return;
    }

    onEdit?.({
      value: date.label,
      tag: date.id,
      name: name,
      record: record,
      textchanged: "0",
      setValue: setValue,
      setInputValue: setInputValue,
      type: module,
      requestId: requestId,
      addInfo: addInfo,
    });
  }

  return (
    <div className={clsx(dialogStyles.mainDialogBlock)}>
      <div className={clsx(dialogStyles.fullDialogFlexContainer)}>
        <div className={clsx(dialogStyles.dialogCalendarContainer)}>
          <div className={clsx(dialogStyles.dialogCalendarMonthsContainer)}>
            <MonthButton title="Январь" id="1" onClick={monthSelect} />
            <MonthButton title="Февраль" id="2" onClick={monthSelect} />
            <MonthButton title="Март" id="3" onClick={monthSelect} />
            <MonthButton title="Апрель" id="4" onClick={monthSelect} />
            <MonthButton title="Май" id="5" onClick={monthSelect} />
            <MonthButton title="Июнь" id="6" onClick={monthSelect} />
            <MonthButton title="Июль" id="7" onClick={monthSelect} />
            <MonthButton title="Август" id="8" onClick={monthSelect} />
            <MonthButton title="Сентябрь" id="9" onClick={monthSelect} />
            <MonthButton title="Октябрь" id="10" onClick={monthSelect} />
            <MonthButton title="Ноябрь" id="11" onClick={monthSelect} />
            <MonthButton title="Декабрь" id="12" onClick={monthSelect} />
          </div>
          <div ref={calendar}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ru">
              <DateCalendar
                showDaysOutsideCurrentMonth
                views={["year", "month", "day"]}
                value={dayjs(getDateForDateObj(dateValue))}
                onChange={(newValue, comand) => {
                  setDateValue(
                    `${newValue.get("date") < 10 ? `0${newValue.get("date")}` : newValue.get("date")}.${
                      newValue.month() + 1 < 10 ? `0${newValue.month() + 1}` : newValue.month() + 1
                    }.${newValue.year()}`,
                  );
                }}
              />
            </LocalizationProvider>
          </div>
          <div className={clsx(dialogStyles.dialogCalendarToolsContainer)}>
            <CalendarToolButton title="В начало года" onClick={onStartYear} icon={<KeyboardDoubleArrowLeftIcon />} />
            <CalendarToolButton title="Текущая дата" onClick={onCurrentDate} icon={<TodayIcon />} />
            <CalendarToolButton title="В конец года" onClick={onEndYear} icon={<KeyboardDoubleArrowRightIcon />} />
          </div>
        </div>
      </div>
    </div>
  );
}
